import React from 'react';
import { Star, X, Trash2 } from 'lucide-react';

/**
 * EmailSavedSearchesModal — list + create user-defined search filters.
 *
 * Pure presentational component. All state owned by `pages/Email.jsx`.
 * Extracted from the 1200-line Email.jsx monolith (Wave 1u3 refactor step 2).
 *
 * Props:
 *  - open: visibility flag
 *  - onClose(): close handler
 *  - savedSearches: array of {id, name, folder, query}
 *  - currentSearch: the active search query (for the "save current" hint)
 *  - newSavedSearchName: controlled input value
 *  - onChangeName(value): input change handler
 *  - onApply(search): apply a saved search
 *  - onDelete(id): remove a saved search
 *  - onSave(): save the current search under the chosen name
 */
export default function EmailSavedSearchesModal({
  open,
  onClose,
  savedSearches = [],
  currentSearch = '',
  newSavedSearchName = '',
  onChangeName,
  onApply,
  onDelete,
  onSave,
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="email-saved-searches-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-bold text-[#1A1B2E] flex items-center gap-2">
            <Star className="w-4 h-4" /> Mes filtres
          </h3>
          <button onClick={onClose} className="text-[#8E8FA5] hover:text-[#1A1B2E]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {savedSearches.length > 0 ? (
          <ul className="space-y-1.5 mb-4">
            {savedSearches.map((s) => (
              <li key={s.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#EFF0F6] hover:border-[#C7D2FE]">
                <button
                  onClick={() => onApply?.(s)}
                  className="flex-1 text-left"
                  data-testid={`email-apply-saved-${s.id}`}
                >
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E]">{s.name}</div>
                  <div className="text-[10.5px] text-[#8E8FA5] font-mono">{s.folder} · {s.query}</div>
                </button>
                <button
                  onClick={() => onDelete?.(s.id)}
                  title="Supprimer"
                  data-testid={`email-delete-saved-${s.id}`}
                  className="text-[#B91C1C] opacity-60 hover:opacity-100"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[12px] text-[#8E8FA5] mb-4 italic">
            Aucune recherche enregistrée. Saisissez une requête (ex: <code>is:unread from:client</code>) puis enregistrez-la ci-dessous.
          </div>
        )}

        <div className="pt-3 border-t border-[#EFF0F6]">
          <h4 className="text-[11px] font-bold uppercase tracking-wide text-[#4F46E5] mb-2">Enregistrer la recherche actuelle</h4>
          <div className="text-[11px] text-[#6B6C85] mb-1.5">
            Requête active : <code className="text-[#4F46E5]">{currentSearch || "(vide — saisissez une recherche d'abord)"}</code>
          </div>
          <div className="flex gap-1.5">
            <input
              value={newSavedSearchName}
              onChange={(e) => onChangeName?.(e.target.value)}
              placeholder="Nom (ex: Devis en attente)"
              data-testid="email-new-saved-name"
              className="flex-1 h-9 px-3 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12px]"
            />
            <button
              onClick={onSave}
              data-testid="email-save-search-btn"
              className="h-9 px-3 rounded-lg brand-gradient text-white text-[12px] font-semibold"
            >Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );
}
